import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  ReactNode,
} from "react";
import { api, setAuthToken } from "../lib/api";
import { AuthUser, HealthResponse, LoginResponse, MeResponse } from "../types/api";

interface AuthContextValue {
  user: AuthUser | null;
  token: string | null;
  authEnabled: boolean;
  loading: boolean;
  login: (username: string, password: string) => Promise<void>;
  logout: () => void;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);
const STORAGE_KEY = "auth_state";

interface StoredAuth {
  token: string;
  user: AuthUser;
}

function decodeExp(token: string): number | null {
  try {
    const [, payload] = token.split(".");
    const decoded = JSON.parse(atob(payload));
    return typeof decoded.exp === "number" ? decoded.exp * 1000 : null;
  } catch {
    return null;
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [token, setToken] = useState<string | null>(null);
  const [user, setUser] = useState<StoredAuth["user"] | null>(null);
  const [authEnabled, setAuthEnabled] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    const bootstrap = async () => {
      try {
        setLoading(true);
        setAuthToken(null);
        const health = await api.get<HealthResponse>("/health");
        const enabled = !!health.data.auth_enabled;
        if (cancelled) return;
        setAuthEnabled(enabled);

        if (!enabled) {
          localStorage.removeItem(STORAGE_KEY);
          setToken(null);
          setUser({ id: "guest", username: "guest", role: "guest" });
          return;
        }

        const stored = localStorage.getItem(STORAGE_KEY);
        if (!stored) {
          setToken(null);
          setUser(null);
          return;
        }

        const parsed = JSON.parse(stored) as StoredAuth;
        const exp = decodeExp(parsed.token);
        if (exp && exp <= Date.now()) {
          localStorage.removeItem(STORAGE_KEY);
          setToken(null);
          setUser(null);
          setAuthToken(null);
          return;
        }

        setAuthToken(parsed.token);
        try {
          const me = await api.get<MeResponse>("/me");
          if (cancelled) return;
          setToken(parsed.token);
          setUser(me.data.user);
        } catch {
          localStorage.removeItem(STORAGE_KEY);
          setToken(null);
          setUser(null);
          setAuthToken(null);
        }
      } catch {
        if (cancelled) return;
        setAuthEnabled(false);
        setToken(null);
        setUser({ id: "guest", username: "guest", role: "guest" });
        setAuthToken(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    bootstrap();

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (authEnabled && token && user) {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({ token, user }));
      setAuthToken(token);
    }
  }, [authEnabled, token, user]);

  const login = async (username: string, password: string) => {
    const res = await api.post<LoginResponse>("/login", { username, password });
    setAuthEnabled(!!res.data.auth_enabled);
    if (res.data.auth_enabled) {
      setToken(res.data.token);
      setUser(res.data.user);
      setAuthToken(res.data.token);
      return;
    }
    setToken(null);
    setUser(res.data.user);
    setAuthToken(null);
  };

  const logout = () => {
    void api.post("/logout").catch(() => undefined);
    setToken(null);
    localStorage.removeItem(STORAGE_KEY);
    setAuthToken(null);
    setUser(authEnabled ? null : { id: "guest", username: "guest", role: "guest" });
  };

  const value = useMemo(
    () => ({ user, token, authEnabled, loading, login, logout }),
    [user, token, authEnabled, loading],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
